import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Palette, Save, ListChecks, RotateCcw, Loader2, AlarmClock, Plus, Trash2, Bookmark } from "lucide-react";

function AdmissionCriteria({ tenant, setTenantBranding }) {
  const [docs, setDocs] = useState((tenant?.admission_criteria?.required_documents || ["CV", "Degree Certificate", "Academic Transcript"]).join("\n"));
  const [minQual, setMinQual] = useState(tenant?.admission_criteria?.min_qualification || "");
  const [progs, setProgs] = useState(() => Object.entries(tenant?.admission_criteria?.programmes || {}).map(([name, v]) => ({
    name, docs: (v?.required_documents || []).join("\n"), minQual: v?.min_qualification || "",
  })));
  const [templates, setTemplates] = useState(tenant?.admission_criteria?.templates || {});
  const [saving, setSaving] = useState(false);
  const [tplRow, setTplRow] = useState(null); // row index being saved as template
  const [tplName, setTplName] = useState("");

  const addProg = () => setProgs([...progs, { name: "", docs: "", minQual: "" }]);
  const updProg = (i, k, val) => setProgs(progs.map((p, idx) => idx === i ? { ...p, [k]: val } : p));
  const rmProg = (i) => setProgs(progs.filter((_, idx) => idx !== i));
  const PRESETS = {
    masters: { docs: "CV\nDegree Certificate\nAcademic Transcript", qual: "A relevant Bachelor's or Honours degree (2:1 or equivalent) from a recognised institution." },
    phd: { docs: "CV\nDegree Certificate\nAcademic Transcript\nResearch Proposal\nTwo Academic References", qual: "A relevant Master's degree (by research or coursework) from a recognised institution." },
    mphil: { docs: "CV\nDegree Certificate\nAcademic Transcript\nResearch Proposal\nOne Academic Reference", qual: "A relevant Bachelor's/Honours degree (upper second class or equivalent) with demonstrated research potential." },
    pgdip: { docs: "CV\nDegree Certificate\nAcademic Transcript", qual: "A relevant Bachelor's degree or equivalent professional qualification from a recognised institution." },
  };
  const applyPreset = (i, p) => setProgs(progs.map((row, idx) => idx === i ? { ...row, docs: p.docs, minQual: p.qual } : row));
  const applyTemplate = (i, name) => {
    const t = templates[name]; if (!t) return;
    setProgs(progs.map((row, idx) => idx === i ? { ...row, docs: (t.required_documents || []).join("\n"), minQual: t.min_qualification || "" } : row));
  };

  const buildCriteria = (tpls) => {
    const required_documents = docs.split("\n").map((s) => s.trim()).filter(Boolean);
    const programmes = {};
    for (const p of progs) {
      const nm = p.name.trim(); if (!nm) continue;
      programmes[nm] = { required_documents: p.docs.split("\n").map((s) => s.trim()).filter(Boolean), min_qualification: p.minQual.trim() };
    }
    return { required_documents, min_qualification: minQual.trim(), programmes, templates: tpls };
  };
  const persist = async (tpls) => {
    const criteria = buildCriteria(tpls);
    if (criteria.required_documents.length === 0) { toast.error("Add at least one default required document."); return false; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/tenants/${tenant.id}`, { admission_criteria: criteria });
      setTenantBranding(data); return true;
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); return false; }
    finally { setSaving(false); }
  };
  const save = async () => { if (await persist(templates)) toast.success("Admission criteria updated"); };
  const saveTemplate = async () => {
    const nm = tplName.trim(); if (!nm) { toast.error("Give the template a name."); return; }
    const row = progs[tplRow];
    const next = { ...templates, [nm]: { required_documents: row.docs.split("\n").map((s) => s.trim()).filter(Boolean), min_qualification: row.minQual.trim() } };
    setTemplates(next);
    if (await persist(next)) { toast.success(`Template "${nm}" saved`); setTplRow(null); setTplName(""); }
  };
  const rmTemplate = async (name) => {
    const next = { ...templates }; delete next[name]; setTemplates(next);
    if (await persist(next)) toast.success(`Template "${name}" removed`);
  };
  const tplNames = Object.keys(templates);

  return (
    <div className="space-y-4">
      <Card className="p-6 space-y-4">
        <div className="flex items-center gap-2"><ListChecks className="h-4 w-4 brand-text" /><h3 className="font-heading font-semibold">Default admission criteria</h3></div>
        <p className="text-sm text-muted-foreground">Applied to any programme without its own override. Drives the completeness check and the AI eligibility review.</p>
        <div><Label>Required documents (one per line)</Label><Textarea data-testid="set-required-docs" rows={4} value={docs} onChange={(e) => setDocs(e.target.value)} /></div>
        <div><Label>Minimum qualification guidance</Label><Textarea data-testid="set-min-qual" rows={3} value={minQual} onChange={(e) => setMinQual(e.target.value)} placeholder="e.g. Master's: a relevant Bachelor's/Honours; PhD: a relevant Master's" /></div>
      </Card>

      {tplNames.length > 0 && (
        <Card className="p-6 space-y-3" data-testid="saved-templates">
          <div className="flex items-center gap-2"><Bookmark className="h-4 w-4 brand-text" /><h3 className="font-heading font-semibold">Saved criteria templates</h3></div>
          <p className="text-sm text-muted-foreground">Reusable templates you can apply to any programme row below.</p>
          <div className="flex flex-wrap gap-2">
            {tplNames.map((nm) => (
              <span key={nm} data-testid={`template-chip-${nm}`} className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/40 px-3 py-1 text-sm">
                {nm}
                <button data-testid={`template-remove-${nm}`} onClick={() => rmTemplate(nm)} className="text-red-500 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></button>
              </span>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-6 space-y-4" data-testid="programme-overrides">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold">Per-programme overrides</h3>
          <Button data-testid="add-programme" size="sm" variant="outline" onClick={addProg}><Plus className="h-4 w-4 mr-1" />Add programme</Button>
        </div>
        <p className="text-sm text-muted-foreground">Set different requirements for a specific programme (matched by exact programme name on the application).</p>
        {progs.length === 0 && <p className="text-sm text-muted-foreground">No programme-specific rules yet — the defaults apply everywhere.</p>}
        {progs.map((p, i) => (
          <div key={i} className="rounded-lg border border-border p-4 space-y-3" data-testid={`programme-row-${i}`}>
            <div className="flex items-center gap-2">
              <Input data-testid={`prog-name-${i}`} placeholder="Programme name (exact)" value={p.name} onChange={(e) => updProg(i, "name", e.target.value)} />
              <Button size="icon" variant="ghost" data-testid={`prog-remove-${i}`} onClick={() => rmProg(i)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground self-center">Quick fill:</span>
              <Button size="sm" variant="secondary" data-testid={`prog-preset-masters-${i}`} onClick={() => applyPreset(i, PRESETS.masters)}>Master's</Button>
              <Button size="sm" variant="secondary" data-testid={`prog-preset-phd-${i}`} onClick={() => applyPreset(i, PRESETS.phd)}>PhD</Button>
              <Button size="sm" variant="secondary" data-testid={`prog-preset-mphil-${i}`} onClick={() => applyPreset(i, PRESETS.mphil)}>MPhil</Button>
              <Button size="sm" variant="secondary" data-testid={`prog-preset-pgdip-${i}`} onClick={() => applyPreset(i, PRESETS.pgdip)}>PGDip</Button>
              {tplNames.length > 0 && (
                <Select onValueChange={(v) => applyTemplate(i, v)}>
                  <SelectTrigger data-testid={`prog-apply-template-${i}`} className="h-8 w-[180px] text-xs"><SelectValue placeholder="Apply saved template" /></SelectTrigger>
                  <SelectContent className="bg-white">{tplNames.map((nm) => <SelectItem key={nm} value={nm}>{nm}</SelectItem>)}</SelectContent>
                </Select>
              )}
              <Button size="sm" variant="outline" data-testid={`prog-save-template-${i}`} onClick={() => { setTplRow(i); setTplName(""); }}><Bookmark className="h-3.5 w-3.5 mr-1" />Save as template</Button>
            </div>
            <div><Label className="text-xs">Required documents (one per line)</Label><Textarea data-testid={`prog-docs-${i}`} rows={3} value={p.docs} onChange={(e) => updProg(i, "docs", e.target.value)} /></div>
            <div><Label className="text-xs">Minimum qualification</Label><Textarea data-testid={`prog-qual-${i}`} rows={2} value={p.minQual} onChange={(e) => updProg(i, "minQual", e.target.value)} /></div>
          </div>
        ))}
      </Card>

      <Button data-testid="set-admission-save" onClick={save} disabled={saving} className="brand-bg text-white">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" />Save Criteria</>}
      </Button>

      <Dialog open={tplRow !== null} onOpenChange={(v) => !v && setTplRow(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Save as reusable template</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Template name</Label>
            <Input data-testid="template-name-input" value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="e.g. Science Master's, Standard PhD" />
            <p className="text-xs text-muted-foreground">Saves this row's required documents and minimum qualification so you can apply them to any programme later.</p>
          </div>
          <DialogFooter>
            <Button data-testid="template-save-confirm" onClick={saveTemplate} disabled={saving} className="brand-bg text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save template"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Settings() {
  const { tenant, setTenantBranding } = useAuth();
  if (!tenant) return <p className="text-muted-foreground">No tenant context.</p>;

  return (
    <div className="space-y-6 animate-fade-up max-w-3xl">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Institution Settings</h1>
        <p className="text-muted-foreground">Customize how {tenant.name} appears and how the AI assistant reviews work.</p>
      </div>
      <Tabs defaultValue="branding">
        <TabsList>
          <TabsTrigger value="branding" data-testid="tab-branding">Branding</TabsTrigger>
          <TabsTrigger value="rubrics" data-testid="tab-rubrics">Review Rubrics</TabsTrigger>
          <TabsTrigger value="turnaround" data-testid="tab-turnaround">Turnaround</TabsTrigger>
          <TabsTrigger value="admissions" data-testid="tab-admissions">Admissions</TabsTrigger>
        </TabsList>
        <TabsContent value="branding" className="mt-4"><Branding tenant={tenant} setTenantBranding={setTenantBranding} /></TabsContent>
        <TabsContent value="rubrics" className="mt-4"><Rubrics /></TabsContent>
        <TabsContent value="turnaround" className="mt-4"><Turnaround tenant={tenant} setTenantBranding={setTenantBranding} /></TabsContent>
        <TabsContent value="admissions" className="mt-4"><AdmissionCriteria tenant={tenant} setTenantBranding={setTenantBranding} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Branding({ tenant, setTenantBranding }) {
  const [form, setForm] = useState({
    name: tenant?.name || "", tagline: tenant?.tagline || "", logo_url: tenant?.logo_url || "",
    primary_color: tenant?.primary_color || "#166534", accent_color: tenant?.accent_color || "#EAB308",
  });
  const preview = (patch) => { const next = { ...form, ...patch }; setForm(next); setTenantBranding({ ...tenant, ...next }); };
  const save = async () => {
    try { const { data } = await api.patch(`/tenants/${tenant.id}`, form); setTenantBranding(data); toast.success("Branding updated"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-center gap-2"><Palette className="h-4 w-4 brand-text" /><h3 className="font-heading font-semibold">Identity</h3></div>
      <div><Label>Institution name</Label><Input data-testid="set-name" value={form.name} onChange={(e) => preview({ name: e.target.value })} /></div>
      <div><Label>Tagline</Label><Input data-testid="set-tagline" value={form.tagline} onChange={(e) => preview({ tagline: e.target.value })} /></div>
      <div><Label>Logo URL</Label><Input data-testid="set-logo" value={form.logo_url} onChange={(e) => preview({ logo_url: e.target.value })} /></div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Primary color</Label><Input data-testid="set-primary" type="color" value={form.primary_color} onChange={(e) => preview({ primary_color: e.target.value })} /></div>
        <div><Label>Accent color</Label><Input data-testid="set-accent" type="color" value={form.accent_color} onChange={(e) => preview({ accent_color: e.target.value })} /></div>
      </div>
      <Button data-testid="set-save" onClick={save} className="brand-bg text-white"><Save className="h-4 w-4 mr-1" />Save Branding</Button>
    </Card>
  );
}

function Turnaround({ tenant, setTenantBranding }) {
  const [days, setDays] = useState(tenant?.review_sla_days || 7);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const v = parseInt(days, 10);
    if (!v || v < 1 || v > 90) { toast.error("Enter a threshold between 1 and 90 days."); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/tenants/${tenant.id}`, { review_sla_days: v });
      setTenantBranding(data);
      toast.success("Turnaround threshold updated");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-center gap-2"><AlarmClock className="h-4 w-4 brand-text" /><h3 className="font-heading font-semibold">Review turnaround SLA</h3></div>
      <p className="text-sm text-muted-foreground">Chapters awaiting a supervisor decision for this many days or longer are flagged <span className="text-red-700 font-medium">overdue</span> on dashboards and the analytics leaderboard.</p>
      <div className="max-w-[200px]">
        <Label>Overdue after (days)</Label>
        <Input data-testid="set-sla-days" type="number" min={1} max={90} value={days} onChange={(e) => setDays(e.target.value)} />
      </div>
      <Button data-testid="set-sla-save" onClick={save} disabled={saving} className="brand-bg text-white">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" />Save Threshold</>}
      </Button>
    </Card>
  );
}

function Rubrics() {
  const [cfg, setCfg] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/tenant/rubrics").then((r) => {
      setCfg({ referencing_style: r.data.referencing_style, universal: r.data.universal, stages: r.data.stages });
      setDefaults(r.data.defaults);
    }).catch(() => {});
  }, []);

  if (!cfg) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin brand-text" /></div>;

  const setStage = (k, text) => setCfg({ ...cfg, stages: { ...cfg.stages, [k]: text.split("\n") } });
  const resetDefaults = () => setCfg({ referencing_style: defaults.referencing_style, universal: defaults.universal, stages: defaults.stages });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/tenant/rubrics", {
        referencing_style: cfg.referencing_style,
        universal: cfg.universal.map((s) => s.trim()).filter(Boolean),
        stages: Object.fromEntries(Object.entries(cfg.stages).map(([k, v]) => [k, v.map((s) => s.trim()).filter(Boolean)])),
      });
      toast.success("Review rubrics saved — the AI will apply them on new submissions");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><ListChecks className="h-4 w-4 brand-text" /><h3 className="font-heading font-semibold">AI Review Rubrics</h3></div>
          <Button variant="outline" size="sm" data-testid="rubrics-reset" onClick={resetDefaults}><RotateCcw className="h-4 w-4 mr-1" />Reset to ARU defaults</Button>
        </div>
        <p className="text-sm text-muted-foreground">The AI assistant applies these criteria (one per line) to each stage. Adjust them to match your institution's guidelines.</p>
        <div>
          <Label>Referencing style</Label>
          <Input data-testid="rubrics-referencing" value={cfg.referencing_style} onChange={(e) => setCfg({ ...cfg, referencing_style: e.target.value })} placeholder="e.g. Harvard, APA, IEEE" className="max-w-xs" />
        </div>
      </Card>

      {Object.entries(cfg.stages).map(([k, v]) => (
        <Card key={k} className="p-5 space-y-2" data-testid={`rubric-stage-${k}`}>
          <Label className="font-heading">{k}</Label>
          <Textarea rows={Math.min(10, Math.max(4, v.length))} value={v.join("\n")} onChange={(e) => setStage(k, e.target.value)}
            data-testid={`rubric-input-${k}`} className="font-mono text-xs" />
        </Card>
      ))}

      <Card className="p-5 space-y-2" data-testid="rubric-universal">
        <Label className="font-heading">Universal criteria (formatting, referencing, ethics, integrity)</Label>
        <Textarea rows={Math.min(10, Math.max(4, cfg.universal.length))} value={cfg.universal.join("\n")}
          onChange={(e) => setCfg({ ...cfg, universal: e.target.value.split("\n") })} className="font-mono text-xs" />
      </Card>

      <Button data-testid="rubrics-save" onClick={save} disabled={saving} className="brand-bg text-white">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" />Save Rubrics</>}
      </Button>
    </div>
  );
}
